import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { useParallax } from '@/hooks/useParallax';
import { MetaTags } from '@/components/MetaTags';
import { Button } from '@/components/ui/button';
import { ContactDialog } from '@/components/ContactDialog';
import { 
  TrendingDown, 
  Sparkles, 
  Calculator, 
  Shield, 
  ArrowRight, 
  Play,
  Clock,
  PiggyBank,
  BarChart3,
  CheckCircle2,
  Zap,
  Users,
  ChevronRight,
  Home,
  Banknote,
  Car,
  GraduationCap,
  Handshake,
  Layers,
  Download,
  HelpCircle
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function Landing() {
  const { scrollY } = useParallax();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <MetaTags />
      
      {/* Animated background elements with parallax */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-accent/8 rounded-full blur-[150px] animate-pulse-glow transition-transform duration-100 ease-out"
          style={{ transform: `translateY(${scrollY * 0.1}px)` }}
        />
        <div 
          className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-accent/8 rounded-full blur-[150px] animate-pulse-glow transition-transform duration-100 ease-out"
          style={{ 
            animationDelay: '1.5s',
            transform: `translateY(${scrollY * -0.15}px)`
          }}
        />
        <div 
          className="absolute top-1/2 left-1/2 w-[1000px] h-[1000px] bg-accent/5 rounded-full blur-[200px] transition-transform duration-100 ease-out"
          style={{ transform: `translate(-50%, calc(-50% + ${scrollY * 0.05}px))` }}
        />
        {/* Grid pattern with subtle parallax */}
        <div 
          className="absolute inset-0 opacity-[0.02] transition-transform duration-100 ease-out"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            transform: `translateY(${scrollY * 0.02}px)`
          }}
        />
        {/* Floating accent elements for depth */}
        <div 
          className="absolute top-[20%] right-[10%] w-2 h-2 bg-accent/30 rounded-full blur-sm"
          style={{ transform: `translateY(${scrollY * 0.25}px)` }}
        />
        <div 
          className="absolute top-[60%] left-[5%] w-3 h-3 bg-accent/20 rounded-full blur-sm"
          style={{ transform: `translateY(${scrollY * -0.2}px)` }}
        />
        <div 
          className="absolute top-[40%] right-[25%] w-1.5 h-1.5 bg-foreground/10 rounded-full blur-sm"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-border/30 bg-background/60 backdrop-blur-xl sticky top-0">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="relative">
                <TrendingDown className="h-7 w-7 text-accent" />
                <div className="absolute inset-0 h-7 w-7 bg-accent/30 blur-lg" />
              </div>
              <span className="text-xl font-display font-bold text-foreground tracking-tight">Recast</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/demo" aria-label="Try Demo" className="hidden sm:block">
                <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                  Try Demo
                </Button>
              </Link>
        <Link to="/auth?mode=signup">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
            Sign Up
          </Button>
        </Link>
        <Link to="/auth">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
            Sign In
          </Button>
        </Link>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main>
        {/* Hero Section */}
        <HeroSection />

        {/* Loan Types Section */}
        <LoanTypesSection />

        {/* Features Section */}
        <FeaturesSection />

        {/* How It Works */}
        <HowItWorksSection />

        {/* Testimonials */}
        <TestimonialsSection />

        {/* FAQ Section */}
        <FAQSection />

        {/* Secondary CTA */}
        <CTASection />
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/30 py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-accent" />
              <span className="font-display font-bold text-foreground">Recast</span>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © {new Date().getFullYear()} Recast. Visualize your path to financial freedom.
            </p>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <ContactDialog />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function HeroSection() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  return (
    <section ref={ref} className="relative z-10 container mx-auto px-4 py-20 md:py-28 lg:py-32">
      <div className="max-w-5xl mx-auto text-center space-y-8">

        {/* Headline */}
        <h1 
          className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-display font-bold text-foreground leading-[1.1] tracking-tight transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          See Your Path to{' '}
          <span 
            className="bg-gradient-to-r from-foreground via-accent to-foreground bg-[length:200%_100%] bg-clip-text text-transparent animate-shimmer"
          >
            Debt Freedom
          </span>
        </h1>

        {/* Subheadline */}
        <p 
          className={`text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Track any loan—mortgage, auto, student, or personal. See exactly how extra payments save you thousands.
        </p>

        {/* Loan Type Icons */}
        <div 
          className={`flex items-center justify-center gap-6 md:gap-8 transition-all duration-700 delay-250 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {[
            { icon: Home, label: 'Mortgage' },
            { icon: Car, label: 'Auto' },
            { icon: GraduationCap, label: 'Student' },
            { icon: Banknote, label: 'Personal' },
            { icon: Handshake, label: 'Business' },
          ].map((loan, index) => (
            <div 
              key={loan.label}
              className="flex flex-col items-center gap-1 opacity-60 hover:opacity-100 transition-opacity"
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <loan.icon className="h-5 w-5 md:h-6 md:w-6 text-accent" />
              <span className="text-xs text-muted-foreground hidden sm:block">{loan.label}</span>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div 
          className={`flex flex-col sm:flex-row items-center justify-center gap-6 pt-4 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <Link to="/demo" aria-label="Try Free Demo">
            <Button 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-medium h-12 px-8 text-base shadow-lg shadow-accent/20 hover:shadow-xl hover:shadow-accent/30 transition-all duration-300 hover:-translate-y-0.5"
            >
              <Play className="h-4 w-4 mr-2" />
              Try Free Demo
            </Button>
          </Link>
          <Link to="/auth" aria-label="Enter My Loan">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-border/50 text-foreground hover:bg-card/80 h-12 px-8 text-base transition-all duration-300 hover:-translate-y-0.5"
            >
              Enter My Loan
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Trust indicators */}
        <div 
          className={`flex items-center justify-center gap-8 pt-8 text-sm text-muted-foreground transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-accent" />
            <span>Bank-level security</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-accent" />
            <span>Real-time calculations</span>
          </div>
        </div>
      </div>
    </section>
  );
}


function LoanTypesSection() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  const loanTypes = [
    { 
      icon: Home, 
      label: 'Mortgage', 
      description: 'Home loans & refinancing',
      slug: 'mortgage'
    },
    { 
      icon: Banknote, 
      label: 'Personal Loan', 
      description: 'Unsecured personal debt',
      slug: 'personal'
    },
    { 
      icon: Car, 
      label: 'Auto Loan', 
      description: 'Vehicle financing',
      slug: 'auto'
    },
    { 
      icon: GraduationCap, 
      label: 'Student Loan', 
      description: 'Education financing',
      slug: 'student'
    },
    { 
      icon: Handshake, 
      label: 'Business Loan', 
      description: 'Commercial financing',
      slug: 'business'
    },
  ];

  return (
    <section ref={ref} className="relative z-10 container mx-auto px-4 py-20">
      <div className="text-center mb-12">
        <h2 
          className={`text-3xl md:text-4xl font-display font-bold text-foreground mb-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Track <span className="text-accent">Any Loan Type</span>
        </h2>
        <p 
          className={`text-muted-foreground max-w-xl mx-auto transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          One powerful platform for all your debt payoff goals
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 max-w-5xl mx-auto">
        {loanTypes.map((loan, index) => (
          <Link
            key={loan.label}
            to={`/loans/${loan.slug}`}
            className="group p-4 md:p-6 rounded-2xl bg-card/40 border border-border/30 backdrop-blur-sm text-center 
              cursor-pointer
              hover:bg-accent/10 hover:border-accent/50 
              hover:-translate-y-2 hover:shadow-xl hover:shadow-accent/20
              transition-all duration-300"
            style={{ 
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: `opacity 700ms ease-out ${index * 80}ms, transform 700ms ease-out ${index * 80}ms, background-color 300ms, border-color 300ms, box-shadow 300ms`
            }}
          >
            <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center mx-auto mb-3 
              group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-accent/30 
              transition-all duration-300">
              <loan.icon className="h-6 w-6 md:h-7 md:w-7 text-accent group-hover:animate-pulse" />
            </div>
            <h3 className="font-display font-semibold text-foreground text-sm md:text-base mb-1">{loan.label}</h3>
            <p className="text-xs text-muted-foreground hidden sm:block">{loan.description}</p>
            
            {/* "Learn More" appears on hover */}
            <div className="mt-2 flex items-center justify-center gap-1 text-xs text-accent 
              opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span>Learn More</span>
              <ChevronRight className="h-3 w-3" />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FeaturesSection() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  const features = [
    // ROW 1 - PRO FEATURES
    {
      icon: TrendingDown,
      title: 'Recast Simulator',
      description: 'See how lump sum payments lower your monthly obligation instantly.',
      isPro: true
    },
    {
      icon: BarChart3,
      title: 'Snowball Strategy',
      description: 'Optimize multiple loans with snowball or avalanche methods.',
      isPro: true
    },
    {
      icon: Layers,
      title: 'Loan Comparison',
      description: 'Compare all loans side-by-side. Prioritize which to pay first.',
      isPro: true
    },
    {
      icon: Download,
      title: 'PDF Export',
      description: 'Export professional amortization schedules to share with your bank.',
      isPro: true
    },
    // ROW 2 - FREE FEATURES
    {
      icon: Calculator,
      title: 'Banker-Grade Math',
      description: 'Real 30/360 and Actual/365 day count methods. No approximations.'
    },
    {
      icon: Clock,
      title: 'Freedom Date Clock',
      description: 'Watch your payoff date move closer with every extra dollar.'
    },
    {
      icon: PiggyBank,
      title: 'Interest Savings',
      description: 'Calculate exactly how much you save with different strategies.'
    },
    {
      icon: Shield,
      title: 'Your Data, Secure',
      description: 'Bank-level encryption. Nothing is ever sold or shared.'
    },
  ];

  return (
    <section ref={ref} className="relative z-10 container mx-auto px-4 py-24">
      <div className="text-center mb-16">
        <h2 
          className={`text-3xl md:text-4xl font-display font-bold text-foreground mb-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Professional Tools, <span className="text-accent">Simple Interface</span>
        </h2>
        <p 
          className={`text-muted-foreground max-w-2xl mx-auto transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Everything you need to optimize your loan payoff strategy
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {features.map((feature, index) => (
          <FeatureCard 
            key={feature.title}
            icon={feature.icon}
            title={feature.title}
            description={feature.description}
            isPro={feature.isPro}
            isVisible={isVisible}
            delay={index * 80}
          />
        ))}
      </div>
    </section>
  );
}

function FeatureCard({ 
  icon: Icon, 
  title, 
  description,
  isPro,
  isVisible,
  delay
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string;
  isPro?: boolean;
  isVisible: boolean;
  delay: number;
}) {
  return (
    <div 
      className={`group p-6 rounded-2xl bg-card/40 border border-border/30 backdrop-blur-sm
        transition-all duration-200 ease-out
        hover:bg-card/60 hover:shadow-lg hover:shadow-accent/10 hover:-translate-y-2 hover:scale-[1.02]
        hover:border-accent/30 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ 
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-accent/20 to-accent/10 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-200">
          <Icon className="h-6 w-6 text-accent group-hover:text-foreground transition-colors duration-200" />
          <div className="absolute inset-0 rounded-xl bg-accent/20 blur-xl opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-200" />
        </div>
        {isPro && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent/20 text-accent border border-accent/30">
            Pro
          </span>
        )}
      </div>
      <h3 className="text-lg font-display font-semibold text-foreground mb-2 group-hover:text-accent transition-colors duration-200">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed group-hover:text-foreground/80 transition-colors duration-200">{description}</p>
    </div>
  );
}

function HowItWorksSection() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  const steps = [
    {
      number: '01',
      title: 'Enter Your Loans',
      description: 'Add any loan type: balance, rate, term. Takes under 60 seconds.'
    },
    {
      number: '02',
      title: 'Explore Strategies',
      description: 'Model extra payments, bi-weekly schedules, and lump sums instantly.'
    },
    {
      number: '03',
      title: 'Watch Freedom Approach',
      description: 'Track progress as years melt away from your debt. Celebrate milestones.'
    },
  ];

  return (
    <section ref={ref} className="relative z-10 container mx-auto px-4 py-24">
      <div className="text-center mb-16">
        <h2 
          className={`text-3xl md:text-4xl font-display font-bold text-foreground mb-4 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          How It Works
        </h2>
        <p 
          className={`text-muted-foreground max-w-xl mx-auto transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          From first login to financial clarity in minutes
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 relative">
          {/* Connection line */}
          <div className="hidden md:block absolute top-12 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-accent/50 via-accent/50 to-accent/50" />
          
          {steps.map((step, index) => (
            <div 
              key={step.number}
              className="group relative text-center cursor-default"
              style={{ 
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 700ms ease-out ${(index + 1) * 150}ms, transform 700ms ease-out ${(index + 1) * 150}ms`
              }}
            >
              <div className="transition-transform duration-200 ease-out group-hover:-translate-y-1">
                <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-card border-2 border-accent/50 mb-6 group-hover:scale-110 group-hover:border-accent transition-all duration-200">
                  <span className="text-xl font-display font-bold text-accent">{step.number}</span>
                  <div className="absolute inset-0 rounded-full bg-accent/10 blur-md group-hover:bg-accent/20 group-hover:blur-lg transition-all duration-200" />
                </div>
                <h3 className="text-lg font-display font-semibold text-foreground mb-3 group-hover:text-accent transition-colors duration-200">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  const testimonials = [
    {
      quote: "Recast showed me I could pay off my mortgage 7 years early just by adding $200/month. The visualization made it click.",
      author: "Sarah M.",
      role: "Homeowner, Texas",
    },
    {
      quote: "Finally, a calculator that uses real banking math. Other tools were always slightly off. Recast matches my statements exactly.",
      author: "David L.",
      role: "Financial Advisor",
    },
    {
      quote: "The loan comparison feature helped us prioritize. We paid off our auto loan first, then snowballed into the mortgage.",
      author: "Jennifer & Mark K.",
      role: "On track to be debt-free",
    },
  ];

  return (
    <section ref={ref} className="relative z-10 py-24 bg-card/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 
            className={`text-3xl md:text-4xl font-display font-bold text-foreground mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Loved by Borrowers
          </h2>
          <p 
            className={`text-muted-foreground transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Join thousands who've accelerated their path to freedom
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div 
              key={testimonial.author}
              className="group p-6 rounded-2xl bg-card/60 border border-border/30 backdrop-blur-sm 
                hover:bg-card/80 hover:border-accent/30 hover:shadow-lg hover:shadow-accent/10 
                hover:-translate-y-2 hover:scale-[1.02]
                transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out"
              style={{ 
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 700ms ease-out ${(index + 1) * 150}ms, transform 200ms ease-out, background-color 200ms ease-out, border-color 200ms ease-out, box-shadow 200ms ease-out`
              }}
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-accent group-hover:scale-110 transition-transform duration-200" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                  </svg>
                ))}
              </div>
              <p className="text-foreground mb-4 leading-relaxed group-hover:text-foreground/90 transition-colors duration-200">"{testimonial.quote}"</p>
              <div>
                <div className="font-medium text-foreground group-hover:text-accent transition-colors duration-200">{testimonial.author}</div>
                <div className="text-sm text-muted-foreground">{testimonial.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  const faqs = [
    {
      question: "Does paying off my loan early really save me money?",
      answer: "Yes, significantly. When you make a standard loan payment, a large portion goes toward interest (the fee for borrowing money) rather than the principal (the actual money you borrowed). By making extra payments or paying the loan off early, you reduce the principal balance faster. Because interest is calculated based on your remaining principal, a lower balance means you accrue less interest in the future."
    },
    {
      question: "Will I be charged a penalty for paying off my loan early?",
      answer: "It depends on your lender. Some lenders charge a prepayment penalty or \"early payoff fee\" to make up for the interest they lose when you pay a loan off ahead of schedule. Before you start an aggressive repayment plan, check your loan contract or call your lender to ask if there are fees for early repayment.\n\nEven if there is a small penalty, the interest you save by paying early often outweighs the fee. Our app helps you run these numbers to ensure you still come out ahead."
    },
    {
      question: "Do extra payments go toward principal or interest?",
      answer: "You want them to go toward the principal. This is a critical distinction. Lenders may sometimes apply extra payments toward \"future interest\" rather than reducing your current balance. To become debt-free faster, you must specify that any extra payment is a principal-only payment. This attacks the debt directly and shortens your loan term."
    },
    {
      question: "Will paying off my loan early hurt my credit score?",
      answer: "You might see a small, temporary dip, but it is good for you in the long run. It sounds counterintuitive, but paying off a loan can close an active credit account, which might slightly lower your credit score by reducing your \"credit mix\" or \"average age of accounts.\" However, this drop is usually temporary. Eliminating debt improves your Debt-to-Income (DTI) ratio, which makes you a more attractive borrower for future mortgages or major loans."
    },
    {
      question: "Should I use the Snowball or Avalanche method to pay off debt?",
      answer: "The \"best\" method is the one you can stick to. Our app supports both strategies, and the choice depends on your personality:\n\nThe Debt Snowball: You pay off the smallest debts first. This gives you quick \"wins\" and psychological momentum.\n\nThe Debt Avalanche: You pay off the loan with the highest interest rate first. This mathematically saves you the most money over time."
    }
  ];

  return (
    <section ref={ref} className="relative z-10 container mx-auto px-4 py-24">
      <div className="text-center mb-16">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6 transition-all duration-700 ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}>
          <HelpCircle className="h-8 w-8 text-accent" />
        </div>
        <h2 className={`text-3xl md:text-4xl font-display font-bold text-foreground mb-4 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}>
          Frequently Asked <span className="text-accent">Questions</span>
        </h2>
        <p className={`text-muted-foreground max-w-xl mx-auto transition-all duration-700 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}>
          Everything you need to know about paying off your loans faster
        </p>
      </div>

      <div className={`max-w-3xl mx-auto transition-all duration-700 delay-200 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}>
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-card/40 border border-border/30 rounded-xl px-6 backdrop-blur-sm hover:bg-card/60 hover:border-accent/30 transition-all duration-200 data-[state=open]:border-accent/50 data-[state=open]:bg-card/60"
            >
              <AccordionTrigger className="text-left text-foreground font-medium py-5 hover:no-underline hover:text-accent [&[data-state=open]]:text-accent">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed whitespace-pre-line pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function CTASection() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  return (
    <section ref={ref} className="relative z-10 container mx-auto px-4 py-24">
      <div 
        className={`max-w-4xl mx-auto text-center p-8 md:p-12 rounded-3xl bg-gradient-to-br from-card/80 to-card/40 border border-border/40 backdrop-blur-sm transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6">
          <Users className="h-8 w-8 text-accent" />
        </div>
        <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
          Ready to Find Your Freedom Date?
        </h2>
        <p className="text-muted-foreground max-w-xl mx-auto mb-8">
          Join like-minded users who've discovered exactly when they'll be debt-free. 
          Start with our free demo—no signup required.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/demo">
            <Button 
              size="lg" 
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-medium h-12 px-8 shadow-lg shadow-accent/20"
            >
              <Play className="h-4 w-4 mr-2" />
              Try Free Demo
            </Button>
          </Link>
          <Link to="/auth">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-border/50 text-foreground hover:bg-card h-12 px-8"
            >
              Create Free Account
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
